import { IntercomIdentity } from '@/contexts/intercom-context/types';
import { NavigationGuardStatus } from '@/components/navigation-stack-guard';
import { partnerSupplier } from '@/utils/environment';
import { useAuth } from '@/contexts/auth-context';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useMemo, useRef } from 'react';
import { useSalesPortalEmployeeRole } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-employee-role';

/** Intercom event fired on Sales Portal entry (analytics only). */
export const SALES_PORTAL_ENTERED_EVENT = 'entered_sales_portal';

const ALLOWED_NAVIGATION_GUARD_STATUSES: NavigationGuardStatus[] = [
  NavigationGuardStatus.AUTHENTICATED,
  NavigationGuardStatus.OFFLINE_MODE,
];

interface UseSalesPortalIntercomIdentityParams {
  navigationGuardStatus: NavigationGuardStatus;
  restaurantUUID: string;
  supplierUUID: string;
}

/**
 * Builds the Sales Portal Intercom boot identity, sourced from AuthContext
 * because Sales Portal renders outside UserContextProvider.
 *
 * @returns The boot identity, or null while inputs load, restaurant/supplier
 * are not yet known, the navigation guard hasn't allowed access, or Intercom
 * is disabled.
 */
export const useSalesPortalIntercomIdentity = ({
  navigationGuardStatus,
  restaurantUUID,
  supplierUUID,
}: UseSalesPortalIntercomIdentityParams): IntercomIdentity | null => {
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);
  const {
    email,
    employeeRole,
    loading: isEmployeeRoleLoading,
    loginEmail,
  } = useSalesPortalEmployeeRole(supplierUUID);
  const { loading: isConfigLoading, value: intercomEnabled } =
    useChatSalesPortalDynamicConfigValue(
      supplierUUID,
      'intercom_enabled',
      null,
    );

  // Unix seconds — the `_at` suffix makes Intercom treat this as a date
  const enteredAtRef = useRef(Math.floor(Date.now() / 1000));

  const identity = useMemo<IntercomIdentity | null>(() => {
    if (
      !restaurantUUID ||
      !supplierUUID ||
      !ALLOWED_NAVIGATION_GUARD_STATUSES.includes(navigationGuardStatus) ||
      isConfigLoading ||
      !intercomEnabled ||
      isEmployeeRoleLoading ||
      !employeeRole ||
      !authenticatedUserUUID
    ) {
      return null;
    }

    return {
      companyId: supplierUUID,
      customAttributes: {
        pepper_business_id: restaurantUUID,
        pepper_business_organization_id:
          partnerSupplier.businessOrganizationUUID,
        pepper_employee_role: employeeRole,
        pepper_is_sales_portal_user: true,
        pepper_is_supplier_employee: true,
        pepper_last_sales_portal_entered_at: enteredAtRef.current,
        ...(loginEmail ? { pepper_employee_login_email: loginEmail } : {}),
      },
      email: email ?? undefined,
      userId: authenticatedUserUUID,
    };
  }, [
    authenticatedUserUUID,
    email,
    employeeRole,
    intercomEnabled,
    isConfigLoading,
    isEmployeeRoleLoading,
    loginEmail,
    navigationGuardStatus,
    restaurantUUID,
    supplierUUID,
  ]);

  return identity;
};
